import { Component, Injector, Input, OnInit } from '@angular/core';
import { WidgetResidualCalculationService } from '../../../../../services/WidgetResidualCalculationService';
import { WidgetBaseComponent } from '../../widgetBase';

/**
 * ValuationResultsResidual Widget
 */
@Component({
  selector: 'valuation-results-residual-widget',
  templateUrl: './valuation-results-residual-widget.component.html',
  styleUrls: ['../../common/css/general.scss', './valuation-results-residual-widget.component.scss']
})
export class ValuationResultsResidualWidgetComponent extends WidgetBaseComponent implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
   * Reference list data
   */
  reflistDropDown = {};

  /**
   * Is widget readonly (obsolete?)
   */
  public readonly = false;

  /**
   * Versions Title Override (obsolete?)
   */
  public versionsTitleOverride = 'residualValue';

  /**
   * Report Designer Locale (obsolete?)
   */
  public reportDesignerLocale = null;

  /**
   * Version (obsolete?)
   */
  public version = null;

  /**
  * Constructor
  * @param widgetResidualCalculationService WidgetResidualCalculationService
  */
  constructor(injector: Injector, private widgetResidualCalculationService: WidgetResidualCalculationService) {
    super(injector, 'valuationResultsResidual', false);
    //override default translationName (which equals to name)
    this.translationName = 'residualValueCalculation';
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.initWidgetConfig();
    this.widgetResized();
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.onEvents.push(this.eventBus.subscribeEvent('saveChanges', () => {
      if (!this.isDirty) {
        this.saveChangesHandled();
      } else {
        this.widgetResidualCalculationService.save().then(() => {
          this.setDirtyFlag(false);
          this.saveChangesHandled();
        });
      }
    }));
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    if (!this.widgetConfig.hashtagSupported) {
      this.widgetConfig.hashtagSupported = true;
    }
    this.currentCalculation = this.currentCalculation || this.widgetConfig.widgetSettings.currentCalculation;
    this.availableCalculations = this.widgetConfig.widgetSettings.availableCalculations;
    this.readonly = this.widgetConfig.widgetSettings.readonly;

    this.onEvents.push(
      this.eventBus.subscribeEvent('editModeToggled', () => {
        setTimeout(() => {
          this.ref.markForCheck();
        });
      })
    );

    this.mockup ?
      this.widgetResidualCalculationService.getMockupData().then((data: any) => {
        this.widgetData = this.preprocessLoadedData(data);
        this.onFieldChanged(); //Re-calc mockup data
      })
      : this.widgetResidualCalculationService.load(this.getReport()).then((data: any) => {
        this.widgetData = this.preprocessLoadedData(data);
        this.page.widgetDataLoaded(this.widgetConfig.widgetSettings.id);
      });
  }

  /**
   * Called by base class to let widget preprocess loaded data if required
   */
  preprocessLoadedData(data) {
    this.setDirtyFlag(false);
    return data[this.currentCalculation];
  }

  /**
   * Called by base class in save process. Widget should return object to be saved
   */
  async provideSaveObject(): Promise<any> {
    return this.widgetData;
  }

  /**
   * Called by base class to inform widget about save action result
   * @param _success {boolean} The result of save action
   * @param _err {any} The error object, if save action failed
   */
  saveCompleted(_success: boolean, _err: any = null): void {
    //do something if error
  }

  /**
   * Save function - obsolete?
   */
  private save() {
    this.widgetResidualCalculationService.save();
  }

  /**
   * Calculation changed event handler
  * @param newCalculation {number} New Calculation
   */
  public calculationChanged(newCalculation: number): void {
    try {
      if (this.widgetConfig?.widgetSettings?.currentCalculation) {
        this.widgetConfig.widgetSettings.currentCalculation = newCalculation;
        this.currentCalculation = newCalculation;

        this.widgetData = null;
        setTimeout(() => {
          this.mockup ?
            this.widgetResidualCalculationService.getMockupData().then((data: any) => {
              this.widgetData = this.preprocessLoadedData(data);
            })
            : this.widgetResidualCalculationService.load(this.getReport()).then((data: any) => {
              this.widgetData = this.preprocessLoadedData(data);
            });
          this.setDirtyFlag(false);
        });
      }
    } catch (e) {
      // Nothing
    }
  }

  /**
   * TrackBy function used by Angular *ngFor trackBy
   * @param _index {number} Index
   * @param el {any} Element
   */
  public trackById(_index: number, el: any): number {
    return el.trackId;
  }

  /**
   * ValuationResultsResidual Add clicked event handler
   */
  public valuationResultsResidualAddClicked() {
    const row = {
      Oid: -1,
      ReportId: this.getReport(),
      Description: "",
      MonthlyRent: 0,
      Months: 0,
      DiscountRate: 0,
      Total: 0
    };

    // add to observable array
    this.widgetData.DiscountedRemainingRentalIncome.push(row);

    this.setDirtyFlag(true);
    this.onFieldChanged();
  }

  /**
   * ValuationResultsResidual Delete clicked event handler
   */
  public valuationResultsResidualDeleteClicked() {
    const checkedRows = this.widgetData.DiscountedRemainingRentalIncome.filter((val) => {
      return val.checked;
    });
    checkedRows.forEach(row => {
      this.widgetData.DiscountedRemainingRentalIncome = this.widgetData.DiscountedRemainingRentalIncome.filter((item) => {
        return item !== row
      });
    });
    this.setDirtyFlag(true);
    this.onFieldChanged();
  }

  /**
   * OtherSurchargesDeductions Add clicked event handler
   */
  public otherSurchargesDeductionsAddClicked() {
    const row = {
      Oid: -1,
      ReportId: this.getReport(),
      Description: '',
      Value: 0
    };

    // add to observable array
    this.widgetData.OtherSurchargesDeductions.push(row);
    this.setDirtyFlag(true);
    this.onFieldChanged();
  }

  /**
   * OtherSurchargesDeductions Delete clicked event handler
   */
  public otherSurchargesDeductionsDeleteClicked() {
    const checkedRows = this.widgetData.OtherSurchargesDeductions.filter((val) => {
      return val.checked;
    });
    checkedRows.forEach(row => {
      this.widgetData.OtherSurchargesDeductions = this.widgetData.OtherSurchargesDeductions.filter((item) => {
        return item !== row
      });
    });
    this.setDirtyFlag(true);
    this.onFieldChanged();
  }

  /**
   * Field changed event handler
   */
  public onFieldChanged() {
    this.widgetData = this.widgetResidualCalculationService.calculate(this, null);
  }
}
